import {IWspUiEnv, Wsp} from "lib/wsp/wsp";
import {IWsExecFrame} from "lib/core/universe";
import {DOM, JSX} from "lib/commons/xml/dom";
import {EActStage, EDrfState, EDrvState, ESrcField, srcUri} from "lib/wsp/src";
import {ITEM} from "lib/wsp/item";
import {JUser} from "lib/core/user";
import {IInputPeriod} from "back/core/widgets/inputPeriod";
import {IRegPointer} from "lib/commons/registry";
import {Signboard} from "lib/commons/utils/signboard";

/**
 * L'interface ISearchStore abstrait le système de stockage de la requête :
 * - privateFile du profil dédié aux requêtes d'items, de tasks...
 * - dossier dynamique dans le bookmark...
 * - dans le flux XML d'un item...
 */
export interface ISearchStore {
	persistSearch(search: ISearchPersist): Promise<void>
}


/**
 * L'interface ISearchViewResult propose les points d'entée pour visualiser une requete
 */
export interface ISearchViewResult {
	/** affiche/Refresh une requete de recherche donnée
	 * 	@return searchId créé (pertinent si @param searchId null). `undefined` si pas de gestion d'ID (affichage d'une unique requete dans ce contexte ...) */
	viewSearch(whereCrit: Element, searchId: string | null, searchLabel?: string, searchDescription?: string): Promise<string | 'undefined'>

	/** @return retourne la forme XML (UI) de la requete affichée  */
	buildWhereXml(): Element
}

/** Requete : enregistrement dans un profil ou dans une liste pré-établie */
export interface ISearchPersist {

	/** Id interne au système de stockage ISearchStore. */
	id?: string;

	/** Titre éditable de la requête. */
	title?: string;

	/** Titre éditable de la requête. */
	description?: string;

	/** Definition XML <where/> des critères. */
	whereCrit?: Element | ((ctx: IRegPointer<IWspUiEnv>) => Element);
}

/** Requete : enregistrement dans un profil ou dans une liste pré-établie */
export interface IExportColsPersist {

	/** Id interne au système de stockage ISearchStore. */
	id?: string;

	/** Titre éditable de cette liste */
	title?: string;

	/** Description de cette liste. */
	description?: string;

	/** Id des colonnes. à afficher */
	columns: string[],

	/** Non traité */
	params?: Jsonisable,
}


/** Critère de recherche. */
export interface ISearchCrit {
	toDom(): Element | undefined
}

/** Fonction intégrable dans un critère de recherche
 * 	@param holder. Exemple :
 * 		<column name={name}/>
 */
export interface ISearchFunc {
	toDom(holder: Element): Element
}

/** Interface pour peupler les colonnes d'une reqête. */
export interface IColSearchBuilder {
	fillSearchColumns(map: Map<string, string | ISearchFunc>): void
}

export function isColSearchBuilder(c: any): c is IColSearchBuilder {return c && (typeof c.fillSearchColumns === 'function')}


/**
 * Export search : colonne d'export
 */
export class SearchExportColumn<C extends IRegPointer<IWspUiEnv>> extends Signboard<C> {

	protected _colDef: ((ctx: C) => Promise<string | ISearchFunc>) | string | ISearchFunc | null;

	protected _colStatus: ESearchExportColumnStatus;

	/** Specif de la ISearchFunc adsociée à cette colonne */
	setColDef(colDef: ((ctx: C) => Promise<string | ISearchFunc>) | string | ISearchFunc): this {
		this._colDef = colDef;
		return this;
	}

	getColDef(ctx: C): Promise<string | ISearchFunc> | string | ISearchFunc | null {
		if (!this._colDef) return null;
		return typeof this._colDef === "function" ? this._colDef.call(this, ctx) : this._colDef;
	}

	/** Statut d'export de la colonne */
	setExportStatus(status: ESearchExportColumnStatus): this {
		this._colStatus = status;
		return this;
	}

	getExportStatus(ctx: C): ESearchExportColumnStatus {
		return this.isAvailable(ctx) ? (this._colStatus || ESearchExportColumnStatus.exported) : ESearchExportColumnStatus.notAvailable;
	}

	/**
	 * Déclaration de cette colonne dans une map en cours de constuctions
	 * @param ctx
	 * @param map
	 */
	async fillSearchColumn(ctx: C, map: Map<string, string | ISearchFunc>): Promise<void> {
		const colDef = await this.getColDef(ctx);
		if (colDef) map.set(this.getLabel(ctx), colDef);
	}

}

export const enum ESearchExportColumnStatus {
	exported = 'exported',
	exportable = 'exportable',
	notAvailable = 'notAvailable',
}


/**
 * Constructeur d'une requête Xml de recherche d'items ou de tasks.
 * [request]
 *  [select resultType="entries|count" max="200" startOffset=""]
 *    [columns dataKeys="srcUri*srcDt"/]
 *    [column dataKey="..."/]
 *    [column dataKey="..." name="" /]
 *    ...
 *    [column name="..." func="..."]...[/column]
 *    TODO [orderBy key="..."]...
 *  [/select]
 *  [where]
 *    [exp type="..."]...[/exp]
 *    [and]...[/and]
 *    [or]...[/or]
 *    [not]...[/not]
 *  [/where]
 * [/request]
 */
export class SearchRequest {
	resultType?: 'entries' | 'count';
	max?: number;
	startOffset?: number;

	columnsFields?: string;
	columns?: Map<string, null | string | ISearchFunc>;

	where: ISearchCrit;
	whereXml: Element;

	constructor(columns?: string | Map<string, null | string | ISearchFunc>) {
		if (columns instanceof Map) this.columns = columns;
		else if (typeof columns === "string") this.columnsFields = columns;
	}

	addFieldColumns(...field: ESrcField[]): this {
		if (!this.columns) this.columns = new Map();
		for (const f of field) this.columns.set(f, null);
		return this;
	}

	addColumn(key: string, colDef: string | ISearchFunc): this {
		if (!this.columns) this.columns = new Map();
		this.columns.set(key, colDef);
		return this;
	}

	setMax(max: number): this {
		this.max = max;
		return this;
	}

	setResultType(type: 'entries' | 'count'): this {
		this.resultType = type;
		return this;
	}

	setWhere(crit: ISearchCrit): this {
		this.where = crit;
		this.whereXml = null;
		return this;
	}

	setWhereXml(crit: Element): this {
		this.whereXml = crit;
		this.where = null;
		return this;
	}

	toDom(): Element {
		const req = JSX.asXml(() => <request>
			<select resultType={this.resultType} max={this.max} startOffset={this.startOffset}/>
			{this.whereXml ? this.whereXml : <where>{this.where ? this.where.toDom() : undefined}</where>}
		</request>);
		if (this.resultType !== 'count') {
			JSX.asXml(() => {
				const select = req.firstChild;
				if (this.columnsFields) select.appendChild(<columns dataKeys={this.columnsFields}/>);
				this.columns?.forEach((func, name) => {
					if (!func || name === func) select.appendChild(<column dataKey={name}/>);
					else if (typeof func === 'string') select.appendChild(<column name={name} dataKey={func}/>);
					else select.appendChild(func.toDom(<column name={name}/>));
				});
			});
		}
		return req;
	}

	toXml(): string {
		return DOM.ser(this.toDom());
		//return `<request><select max="200"><column dataKey="srcUri"/><column dataKey="itModel"/></select><where><exp type="ItemSrcType" srcTypes="12"/></where></request>`
	}
}


export class SearchAnd extends Array<ISearchCrit> implements ISearchCrit {
	toDom(): Element | undefined {
		const r = <and>{this.map((crit) => crit.toDom())}</and> as Element;
		return r.hasChildNodes() ? r : undefined;
	}
}

export class SearchOr extends Array<ISearchCrit> implements ISearchCrit {
	toDom(): Element | undefined {
		const r = <or>{this.map((crit) => crit.toDom())}</or>;
		return r.hasChildNodes() ? r : undefined;
	}
}

export class SearchNot implements ISearchCrit {
	constructor(public crit: ISearchCrit) {}

	toDom(): Element | undefined {
		const r = this.crit.toDom();
		return r ? <not>{r}</not> : undefined;
	}
}

export class SearchFree implements ISearchCrit {
	constructor(public dom: Element) {}

	toDom(): Element | undefined {return this.dom}
}

export class SearchItemSrcType implements ISearchCrit {
	constructor(public srcTypes: ESearchItemSrcTypes | ESearchItemSrcTypes) {}

	toDom(): Element | undefined {
		return <exp type="ItemSrcType" srcTypes={this.srcTypes}/>;
	}
}

export enum ESearchItemSrcTypes {
	WSP = 1,
	SPACE = 2,
	ITEMFOLDER = 4,
	ITEMFILE = 8,
	RESFOLDER = 16,
	RESFILE = 32
}

export class SearchPath implements ISearchCrit {
	constructor(public path?: string) {}

	toDom(): Element | undefined {return this.path ? <exp type="Path" path={this.path}/> : undefined}
}

export class SearchRootFolder implements ISearchCrit {
	constructor(public path?: string) {}

	toDom(): Element | undefined {return this.path ? <exp type="RootFolder" path={this.path}/> : undefined}
}

export class SearchLinkParents implements ISearchCrit {
	constructor(public path?: string, public linkTraitFilter?: string) {}

	toDom(): Element | undefined {return this.path ? <exp type="LinkParents" path={this.path} linkTraitFilter={this.linkTraitFilter}/> : undefined}
}

export class SearchLinkChildren implements ISearchCrit {
	constructor(public path?: string, public linkTraitFilter?: string) {}

	toDom(): Element | undefined {return this.path ? <exp type="LinkChildren" path={this.path} linkTraitFilter={this.linkTraitFilter}/> : undefined}
}

export class SearchAirItems implements ISearchCrit {
	toDom(): Element | undefined {return <exp type="ItemsInAir"/>}
}

export class SearchForeignItems implements ISearchCrit {
	toDom(): Element | undefined {return <exp type="ItemExternals"/>}
}


export class SearchItemSgnRegexp implements ISearchCrit {
	/** si {@param sgnRegExp} est un tableau, logique OR pour la recherche **/
	constructor(public sgnRegExp?: string | string[]) {}

	toDom(): Element | undefined {return this.sgnRegExp ? <exp type="ItemSgnRegexp" regexpSgn={typeof this.sgnRegExp === 'object' ? this.sgnRegExp.join("|") : this.sgnRegExp}/> : undefined}
}

export class SearchItemModel implements ISearchCrit {
	constructor(public models?: string[]) {}

	toDom(): Element | undefined {return this.models ? <exp type="ItemModel" models={this.models.join(' ')}/> : undefined}
}

export class SearchLastUser implements ISearchCrit {
	constructor(public users?: JUser[] | String[]) {}

	toDom(): Element | undefined {
		if (this.users) {
			const accounts: String[] = [];
			this.users.forEach(user => accounts.push(typeof user === "string" ? user : (user as JUser).account));
			return <exp type="LastUser" users={accounts.join(' ')}/>
		} else
			return undefined;
	}
}


export class SearchOccurencesSubExp implements ISearchCrit {
	constructor(public op: EOperatorsSimple, public value: number = 0, public subExp?: ISearchCrit) {}

	public setSubExp(subExp: ISearchCrit): void {
		this.subExp = subExp;
	}

	toDom(): Element | undefined {
		return this.subExp ? <exp type="OccurencesSubExp" op={this.op} value={this.value}>
			{this.subExp.toDom()}
		</exp> : undefined;
	}
}

export class SearchItems implements ISearchCrit {
	public afterUri?: string;
	public beforeUri?: string;
	public beforeEndFolder?: string;
	public exclude?: Set<string>;
	public excludeExcept?: Set<string>;
	public withDrfErased: boolean;

	inSpace(uri: srcUri): this {
		this.afterUri = uri + '/';
		if (this.beforeUri !== undefined) this.beforeUri = undefined;
		this.beforeEndFolder = this.afterUri;
		return this;
	}

	/** Exclut toutes les uri commençant par "/~". */
	excludeTildeRoot(): this {
		if (this.exclude) {
			for (const u of this.exclude) if (u.startsWith("/~")) this.exclude.delete(u);
		} else {
			this.exclude = new Set();
		}
		this.exclude.add("/~");
		return this;
	}

	isTildeRootExcluded() {
		return this.exclude ? this.exclude.has("/~") : false;
	}

	/** Exclut ou réinclut (si isTildeRootExcluded()) les airItems. */
	excludeAirItems(exclude: boolean): this {
		if (this.isTildeRootExcluded()) {
			if (!exclude) this.excludeExceptSpaces(ITEM.AIR_PREFIX);
			else if (this.excludeExcept) this.excludeExcept.delete(ITEM.AIR_PREFIX);
		} else {
			if (!this.exclude) this.newExcludeSet();
			if (exclude) this.exclude.add(ITEM.AIR_PREFIX);
			else this.exclude.delete(ITEM.AIR_PREFIX);
		}
		return this;
	}

	/** Exclut ou réinclut (si isTildeRootExcluded()) les extItems. */
	excludeExtItems(exclude: boolean): this {
		if (this.isTildeRootExcluded()) {
			if (!exclude) this.excludeExceptSpaces(ITEM.EXT_PREFIX);
			else if (this.excludeExcept) this.excludeExcept.delete(ITEM.EXT_PREFIX);
		} else {
			if (!this.exclude) this.newExcludeSet();
			if (exclude) this.exclude.add(ITEM.EXT_PREFIX);
			else this.exclude.delete(ITEM.EXT_PREFIX);
		}
		return this;
	}

	/** Exclut n'importe quel espace (voir un item) */
	excludeSpaces(...spaceUris: srcUri[]): this {
		if (spaceUris && spaceUris.length > 0) {
			if (!this.exclude) this.newExcludeSet();
			for (const uri of spaceUris) this.exclude.add(uri.endsWith('/') ? uri : uri + '/');
		}
		return this;
	}

	/** Retire d'une exclusion un sous path (sous espace ou item). */
	excludeExceptSpaces(...spaceUris: srcUri[]): this {
		if (spaceUris && spaceUris.length > 0) {
			if (!this.excludeExcept) this.excludeExcept = new Set();
			for (const uri of spaceUris) this.excludeExcept.add(uri.endsWith('/') ? uri : uri + '/');
		}
		return this;
	}

	includeDrfErased(withDrfErased: boolean): this {
		this.withDrfErased = withDrfErased;
		return this;
	}

	protected newExcludeSet() {
		this.exclude = new Set();
		this.exclude.add(ITEM.ANNOT_PREFIX);
	}

	toDom(): Element | undefined {
		return <exp type="Items"
								afterUri={this.afterUri}
								beforeUri={this.beforeUri}
								beforeEndFolder={this.beforeEndFolder}
								exclude={this.exclude ? Array.from(this.exclude).join(';') : ITEM.ANNOT_PREFIX}
								excludeExcept={this.excludeExcept ? Array.from(this.excludeExcept).join(';') : undefined}
								includeDrfErased={this.withDrfErased || undefined}/>
	}
}

export class SearchItemLinks implements ISearchCrit {
	path: string | ISearchFunc;
	linkName?: string;
	linkNamePattern?: string;
	linkTraitFilter?: string;

	constructor(public linkAxis: 'LinkAsc' | 'LinkDesc' | 'LinkChildren' | 'LinkParents') {
	}

	setPath(path: string | ISearchFunc): this {
		this.path = path;
		return this;
	}

	setLinkName(linkName: string): this {
		this.linkName = linkName;
		this.linkNamePattern = undefined;
		this.linkTraitFilter = undefined;
		return this;
	}

	setLinkNamePattern(linkNamePattern: string): this {
		this.linkNamePattern = linkNamePattern;
		this.linkName = undefined;
		this.linkTraitFilter = undefined;
		return this;
	}

	setLinkTraitFilter(linkTraitFilter: string): this {
		this.linkTraitFilter = linkTraitFilter;
		this.linkName = undefined;
		this.linkNamePattern = undefined;
		return this;
	}

	toDom(): Element | undefined {
		if (!this.path) return undefined;
		const elt = <exp type={this.linkAxis} linkName={this.linkName} linkNamePattern={this.linkNamePattern} linkTraitFilter={this.linkTraitFilter}/> as Element;
		if (typeof this.path === 'string') {
			elt.setAttribute("path", this.path || "");
		} else {
			elt.appendChild(this.path.toDom(<path/>));
		}
		return elt;
	}
}

/**
 * SearchFullText
 */
export class SearchFullText implements ISearchCrit {
	constructor(public textSearch: string, public ignoreCase?: boolean, public wholeWord?: boolean, public dataKeyResults?: string, public dataKeyScore?: string) {}

	toDom(): Element | undefined {
		if (!this.textSearch) return undefined;
		let opts: string;
		if (this.ignoreCase) {
			opts = this.wholeWord ? ";c;w;" : ";c;";
		} else if (this.wholeWord) {
			opts = ";w;";
		}
		return <exp type="FullText" textSearch={this.textSearch} options={opts}
								dataKeyResults={this.dataKeyResults} dataKeyScore={this.dataKeyScore}/>;
	}
}

/**
 * SearchRegexpUri
 */
export class SearchRegexpUri implements ISearchCrit {

	constructor(public regexp: RegExp) {}

	toDom(): Element | undefined {
		if (this.regexp) {
			let regExpTxt = this.regexp.flags ? "(?" + this.regexp.flags + ")" : "";
			regExpTxt += this.regexp.source;
			return <exp type="RegexpUri" regexp={regExpTxt}/>;
		}
		return undefined;
	}
}

/**
 * SearchFullPath
 */
export class SearchFullXPath implements ISearchCrit {
	constructor(public xpath: string, public ns: Map<string, string>) {}

	toDom(): Element | undefined {
		if (this.xpath) {
			const exp = <exp type="FullXPath" xpathSearch={this.xpath}/>
			if (this.ns)
				this.ns.forEach((value, key) =>
					exp.appendChild(<ns prefix={key} uri={value}/>)
				)
			return exp;
		}
		return undefined;
	}
}

/**
 * SearchTitleRegexp
 */
export class SearchItemTitleRegexp implements ISearchCrit {
	constructor(public regexp: RegExp) {}

	toDom(): Element | undefined {
		if (this.regexp && this.regexp.source) {
			let regExpTxt = "";
			if (this.regexp.flags) regExpTxt += "(?" + this.regexp.flags + ")";
			regExpTxt += this.regexp.source;
			return <exp type="ItemTitleRegexp" regexp={regExpTxt}/>;
		}
		return undefined;
	}
}

/**
 * SearchItemCodeOrTitle
 */
export class SearchItemCodeOrTitle implements ISearchCrit {
	constructor(public regexp: RegExp) {}

	toDom(): Element | undefined {
		if (this.regexp && this.regexp.source) {
			let regExpTxt = "";
			if (this.regexp.flags) regExpTxt += "(?" + this.regexp.flags + ")";
			regExpTxt += this.regexp.source;
			return <exp type="ItemCodeOrTitle" regexp={regExpTxt}/>;
		}
		return undefined;
	}
}

/**
 * SearchOrphan
 */
export class SearchOrphan implements ISearchCrit {
	toDom(): Element | undefined {
		return <exp type="ItemOrphan"/>;
	}
}

/**
 * SearchStatus
 */
export class SearchStatus implements ISearchCrit {
	constructor(public minStatus: number, public maxStatus: number) {}

	toDom(): Element | undefined {
		return (this.minStatus && this.maxStatus) ? <exp type="ItemStatus" itemStatusMin={this.minStatus} itemStatusMax={this.maxStatus}/> : undefined;
	}
}

/**
 * SearchLastModif
 */
export type EOperatorsSimple = "=" | "!=" | "gt" | "lt" | "gt=" | "lt=";

export type EOperatorsIn = "lt.lt" | "lt=.lt" | "lt.lt=" | "lt=.lt=";

export class SearchLastModif implements ISearchCrit {

	static buildFromInputPeriod(input: IInputPeriod): SearchLastModif | SearchLastModifIn | undefined {
		if (!input || !input.isSpecified()) return undefined;
		const lessThan = input.getLessThan(true);
		const moreThan = input.getMoreThan(true);
		if (moreThan !== null && lessThan !== null) return new SearchLastModifIn("lt.lt", moreThan, lessThan);
		return moreThan !== null ? new SearchLastModif("gt", moreThan) : new SearchLastModif("lt", lessThan);
	}

	constructor(public operator: EOperatorsSimple, public date: Date | number) {};

	toDom(): Element | undefined {
		if (this.date == null || !this.operator) return undefined;
		const vExp = <exp type="LastModif" op={this.operator}/>;
		vExp.setAttribute("value", (typeof this.date === "number" ? this.date : this.date.getTime()).toString());
		return vExp;
	}
}

export class SearchLastModifIn implements ISearchCrit {
	constructor(public operator: EOperatorsIn, public startDate: Date | number, public endDate: Date | number) {};

	toDom(): Element | undefined {
		if (!this.operator || this.startDate == null || this.endDate == null) return undefined;
		return <exp type="LastModif" op={this.operator} value={
			(typeof this.startDate === "number" ? this.startDate : this.startDate.getTime()).toString()
			+ ","
			+ (typeof this.endDate === "number" ? this.endDate : this.endDate.getTime()).toString()
		}/>;
	}
}


/**
 * SearchLifeCycleStates
 */
export class SearchLifeCycleStates implements ISearchCrit {

	static buildFromInputPeriod(input: IInputPeriod): SearchLifeCycleStates | undefined {
		if (!input || !input.isSpecified()) return undefined;
		const lessThan = input.getLessThan(true);
		const moreThan = input.getMoreThan(true);
		if (moreThan !== null && lessThan !== null) return new SearchLifeCycleStates(null, null, "lt.lt", moreThan, lessThan);
		return moreThan !== null ? new SearchLifeCycleStates(null, null, "gt", moreThan) : new SearchLifeCycleStates(null, null, "lt", lessThan);
	}

	constructor(public lcStates?: string[], public lcBys?: string[], public dateOperator?: EOperatorsSimple | EOperatorsIn, public startDate?: Date | number, public endDate?: Date | number) {};

	toDom(): Element | undefined {
		if (!this.lcStates) return undefined;
		const vExp = <exp type="LifeCycleStates" lcStates={this.lcStates.join(' ')} lcBys={this.lcBys} opDt={this.dateOperator}/>;
		if (this.startDate) {
			let lcDate = (typeof this.startDate === "number" ? this.startDate : this.startDate.getTime()).toString();
			if (this.endDate)
				lcDate += "," + (typeof this.endDate === "number" ? this.endDate : this.endDate.getTime()).toString();
			vExp.setAttribute("lcDate", lcDate);
		}
		return vExp;
	}
}


/**
 * SearchResponsibilities
 */
export class SearchResponsibilities implements ISearchCrit {

	constructor(public responsibilities: string[], public accounts?: string[], public toMembers: boolean = false, public toGroups: boolean = false) {};

	toDom(): Element | undefined {
		if (!this.responsibilities) return undefined;
		const expands = [];
		if (this.toMembers) expands.push("toMembers");
		if (this.toGroups) expands.push("toGroups");
		return <exp type="Responsibilities" responsibilities={this.responsibilities?.join(' ')} users={this.accounts?.join(' ')} expandUsers={expands}/>;
	}
}

/**
 * SearchItemComment
 */
export class SearchItemComment implements ISearchCrit {
	constructor(public withOpenComments: boolean, public withClosedComments: boolean) {};

	toDom(): Element | undefined {
		if (this.withClosedComments || this.withOpenComments) {
			let XPathSearch = "descendant::comment()[matchesRegex(normalize-space(.), '<comment.*')]";
			if (this.withClosedComments && !this.withOpenComments)
				XPathSearch += "[matchesRegex(normalize-space(.), '<comment[^>]*threadClosed=.true.[^>]*>.*')]";
			else if (!this.withClosedComments && this.withOpenComments)
				XPathSearch += "[not(matchesRegex(normalize-space(.), '<comment[^>]*threadClosed=.true.[^>]*>.*'))]";
			return <exp type="FullXPath" xpathSearch={XPathSearch}/>;
		}
	}
}

export type EItemPropsMultiValued = 'atLeastOne' | 'all' | 'firstValue';

/**
 * SearchItemPropsRegexp
 */
export class SearchItemPropsRegexp implements ISearchCrit {

	constructor(public propName: string, public regexp: RegExp, public multiValued: EItemPropsMultiValued = 'atLeastOne') {}

	toDom(): Element | undefined {
		if (this.regexp) {
			let regExpTxt = this.regexp.flags ? "(?" + this.regexp.flags + ")" : "";
			regExpTxt += this.regexp.source;
			return <exp type="ItemPropsRegexp" propName={this.propName} regexp={regExpTxt} multiValued={this.multiValued}/>;
		}
		return undefined;
	}
}

/**
 * SearchItemPropsNumber
 */
export class SearchItemPropsNumber implements ISearchCrit {

	constructor(public propName: string, public op: EOperatorsSimple, public value: number, public multiValued: EItemPropsMultiValued = 'atLeastOne') {}

	toDom(): Element | undefined {
		if (Number.isInteger(this.value)) {
			return <exp type="ItemPropsNumber" propName={this.propName} op={this.op} value={this.value} multiValued={this.multiValued}/>;
		}
		return undefined;
	}
}

/**
 * SearchItemPropsDate
 */
export class SearchItemPropsDate implements ISearchCrit {

	static buildFromInputPeriod(input: IInputPeriod, propName: string, multiValued?: EItemPropsMultiValued): SearchItemPropsDate | undefined {
		if (!input || !input.isSpecified()) return undefined;
		const lessThan = input.getLessThan(true);
		const moreThan = input.getMoreThan(true);
		if (moreThan !== null && lessThan !== null) return new SearchItemPropsDate(propName, "lt.lt", moreThan, lessThan, multiValued);
		return moreThan !== null ? new SearchItemPropsDate(propName, "gt", moreThan, null, multiValued) : new SearchItemPropsDate(propName, "lt", lessThan, null, multiValued);
	}

	constructor(public propName: string, public op?: EOperatorsSimple | EOperatorsIn, public startDate?: Date | number, public endDate?: Date | number, public multiValued: EItemPropsMultiValued = 'atLeastOne') {};

	toDom(): Element | undefined {
		if (!this.startDate) return undefined;
		const vExp = <exp type="ItemPropsDate" propName={this.propName} multiValued={this.multiValued} op={this.op}/>;
		let date = (typeof this.startDate === "number" ? this.startDate : this.startDate.getTime()).toString();
		if (this.endDate)
			date += "," + (typeof this.endDate === "number" ? this.endDate : this.endDate.getTime()).toString();
		vExp.setAttribute("value", date);
		return vExp;
	}
}

/**
 * SearchDrfStates
 */
export class SearchDrfStates implements ISearchCrit {
	constructor(public drfStates: EDrfState[]) {};

	toDom(): Element | undefined {
		if (this.drfStates && this.drfStates.length > 0) {
			return <exp type="DrfStates" drfStates={this.drfStates.join(" ")}/>;
		}
		return undefined;
	}
}

/**
 * SearchDrvStates
 */
export class SearchDrvStates implements ISearchCrit {
	constructor(public drvStates: EDrvState[]) {};

	toDom(): Element | undefined {
		if (this.drvStates && this.drvStates.length > 0) {
			return <exp type="DrvStates" drvStates={this.drvStates.join(" ")}/>;
		}
		return undefined;
	}
}


//-----------  SearchFunc (valeurs des fields)  -------------


/**
 * Construction d'une valeur via une func free donnée
 */
export class SearchFuncFree implements ISearchFunc {

	/**
	 * @param funcDef : string (formal xml sérialisé)
	 */
	constructor(public funcDef: string | Element) {}

	toDom(holder: Element): Element {
		const freeElt = this.funcDef instanceof Element ? this.funcDef : DOM.parseDom(this.funcDef).documentElement;
		if (freeElt) {
			for (const att of freeElt.attributes)
				holder.setAttribute(att.name, att.value)
			for (const elt of freeElt.children)
				holder.appendChild(holder.ownerDocument.importNode(elt, true))
		}
		return holder;
	}
}


/**
 * Construction d'une valeur par la valeur d'une propriété donnée, sans modification
 */
export class SearchFuncProps implements ISearchFunc {

	constructor(public dataKey: string) {}

	toDom(holder: Element): Element {
		holder.setAttribute("dataKey", this.dataKey);
		return holder;
	}
}

/**
 * Construction d'une valeur par exécution de xpath
 */
export class SearchFuncXpath implements ISearchFunc {

	constructor(public xpath: string, public ns: Map<string, string>) {}

	toDom(holder: Element): Element {
		if (this.xpath) {
			holder.setAttribute("func", "XPath");
			holder.setAttribute("xpath", this.xpath);
			if (this.ns)
				this.ns.forEach((value, key) =>
					holder.appendChild(<ns prefix={key} uri={value}/>)
				)
		}
		return holder;
	}
}

/**
 * Construction d'une valeur en isolant le `leafName` d'une srcUri fournie par le param `uri`
 */
export class SearchFuncUri2LeafName implements ISearchFunc {

	/**
	 * @param uri
	 * 		string : dataKey fournissant l'URL
	 * 		OU ISearchFunc
	 */
	constructor(public uri: string | ISearchFunc, public trimExtension = false) {}

	setUri(uri: string | ISearchFunc): this {
		this.uri = uri;
		return this;
	}

	toDom(holder: Element): Element {
		holder.setAttribute("func", "Uri2LeafName");
		holder.setAttribute("trimExtension", String(this.trimExtension));
		if (typeof this.uri === 'string') {
			holder.setAttribute("dataKey", this.uri || "");
		} else {
			holder.appendChild(this.uri.toDom(<uri/>));
		}
		return holder;
	}
}

/**
 * Construction d'une cellule en isolant l'espace d'une srcUri fournie par le param `uri`
 */
export class SearchFuncUri2Space implements ISearchFunc {

	/**
	 * @param uri
	 * 		string : dataKey fournissant l'URL
	 * 		OU ISearchFunc
	 */
	constructor(public uri: string | ISearchFunc) {}

	setUri(uri: string | ISearchFunc): this {
		this.uri = uri;
		return this;
	}

	toDom(holder: Element): Element {
		holder.setAttribute("func", "Uri2Space");
		if (typeof this.uri === 'string') {
			holder.setAttribute("dataKey", this.uri || "");
		} else {
			holder.appendChild(this.uri.toDom(<uri/>));
		}
		return holder;
	}

}

/**
 * Construction d'une cellule concaténant n résultats de recherche
 */
export class SearchFuncSubRequest2Csv implements ISearchFunc {

	protected fieldSeparator: string;

	protected lineSeparator: string;

	protected escapeOnlyIfNeeded: boolean = true;

	/**
	 * @param where : critères de recherche
	 */
	constructor(protected select?: Map<string, null | string | ISearchFunc>, protected where?: ISearchCrit | ISearchCrit[]) {}

	setSelect(select: Map<string, null | string | ISearchFunc>): this {
		this.select = select;
		return this;
	}

	setWhere(where: ISearchCrit | ISearchCrit[]): this {
		this.where = where;
		return this;
	}

	setFieldSeparator(fieldSeparator: string): this {
		this.fieldSeparator = fieldSeparator;
		return this;
	}

	setLineSeparator(lineSeparator: string): this {
		this.lineSeparator = lineSeparator;
		return this;
	}

	setEscapeOnlyIfNeeded(escapeOnlyIfNeeded: boolean): this {
		this.escapeOnlyIfNeeded = escapeOnlyIfNeeded;
		return this;
	}

	toDom(holder: Element): Element {
		if (this.where && this.select) {
			holder.setAttribute("func", "SubRequest2Csv");
			if (this.fieldSeparator) holder.setAttribute("fieldSeparator", this.fieldSeparator);
			if (this.lineSeparator) holder.setAttribute("lineSeparator", this.lineSeparator);
			holder.setAttribute("escapeOnlyIfNeeded", String(this.escapeOnlyIfNeeded));
			const req = new SearchRequest(this.select)
			if (Array.isArray(this.where))
				req.setWhere(new SearchAnd(...this.where));
			else
				req.setWhere(this.where)
			const reqDom = req.toDom();
			holder.appendChild(holder.ownerDocument.importNode(reqDom.querySelector('select'), true));
			holder.appendChild(holder.ownerDocument.importNode(reqDom.querySelector('where'), true));
			return holder;
		}
	}

}


/**
 * Construction d'une valeur en isolant le `leafName` d'une srcUri fournie par le param `uri`
 */
export class SearchFuncTranslateStr implements ISearchFunc {

	protected otherValue: string | "" | null = null;

	protected concat: string | null = null;

	protected dict: { [key: string]: string } | (() => { [key: string]: string });

	/**
	 * @param str
	 * 		string : dataKey fournissant l'URL
	 * 		OU ISearchFunc
	 */
	constructor(public str: string | ISearchFunc) {}

	/**
	 * Définit le dictionnaire de translation
	 * @param dict
	 */
	setDict(dict: { [key: string]: string } | (() => { [key: string]: string })): this {
		this.dict = dict;
		return this;
	}

	setStr(str: string | ISearchFunc): this {
		this.str = str;
		return this;
	}

	/**
	 * Définit le caractère de concaténation d'une liste de valeurs transformées
	 * @param char
	 */
	setConcat(char: string): this {
		this.concat = char;
		return this;
	}

	/**
	 * Définit le comportement en cas d'absence de la clef du dictionnaire
	 * @param val
	 * <ul>
	 * 	<li>null : si la clef est inconnue du dictionnaire, alors celle-ci est préservée</li>
	 * 	<li>"" : si la clef est inconnue du dictionnaire, alors celle-ci est supprimée</li>
	 * 	<li>string : si la clef est inconnue du dictionnaire, alors celle-ci est remplacée par cette valeur</li>
	 * </ul>
	 */
	setOtherValue(val: string | "" | null = ""): this {
		this.otherValue = val;
		return this;
	}

	toDom(holder: Element): Element {
		holder.setAttribute("func", "TranslateStr");
		if (this.concat) holder.setAttribute("concat", this.concat);
		if (typeof this.str === 'string')
			holder.setAttribute("dataKey", this.str || "");
		else
			holder.appendChild(this.str.toDom(<string/>));
		const dictElt = holder.appendChild(<dict/>);
		if (this.otherValue !== null)
			dictElt.setAttribute("otherValue", this.otherValue);
		const dict = typeof this.dict === "function" ? this.dict() : this.dict;
		for (const key in dict)
			dictElt.appendChild(<entry from={key} to={dict[key]}/>);
		return holder;
	}
}

/**
 * Construction d'une valeur en mettant en forme un ou plusieurs account (en Prenom + nom)
 */
export class SearchFuncAccountProp implements ISearchFunc {

	protected separator: string = ", ";

	/**
	 * @param accounts
	 * 		string : dataKey fournissant l'account ou la liste d'accounts separés par {@link SearchFuncAccountProp.separator}
	 * 		OU ISearchFunc
	 */
	constructor(public accounts: string | ISearchFunc) {}

	setAccounts(accounts: string | ISearchFunc): this {
		this.accounts = accounts;
		return this;
	}

	/**
	 * Définit le caractère de séparation d'une liste d'accounts (à tokeniser)
	 * @param char
	 */
	setSeparator(separator: string): this {
		this.separator = separator;
		return this;
	}

	toDom(holder: Element): Element {
		holder.setAttribute("func", "AccountPropFunc");
		if (this.separator) holder.setAttribute("separator", this.separator);
		if (typeof this.accounts === 'string')
			holder.setAttribute("dataKey", this.accounts || "");
		else
			holder.appendChild(this.accounts.toDom(<account/>));
		return holder;
	}
}

/**
 * Construction d'une valeur issue d'une propriété d'item
 */
export class SearchFuncItemProp implements ISearchFunc {

	protected eachValues:boolean = false;

	protected concat: string|null = ", ";

	constructor(public propName: string, public propType : 'String'|'Date'|'Number' = "String") {}

	/**
	 * Si true, toutes les valeurs sont retournées. Sous la forme d'une liste concaténée (comportement par défaut),
	 * ou d'une lise d'objets (si separator==null)
	 * @param eachValues
	 */
	setEachValues(eachValues: boolean, separator: string | null): this {
		this.eachValues = eachValues;

		this.concat = separator;
		return this;
	}

	toDom(holder: Element): Element {
		holder.setAttribute("func", "ItemPropFunc");
		holder.setAttribute("propName", this.propName);
		holder.setAttribute("returnType", this.propType);
		holder.setAttribute("eachValues", String(this.eachValues));
		if (this.concat) holder.setAttribute("concat", this.concat);
		return holder;
	}
}


/**
 * Construction d'une cellule présentant une date fournie sous sa forme long
 */
export class SearchFuncLong2Date implements ISearchFunc {
	protected formatDate: ELong2DateStyles | null;
	protected formatTime: ELong2DateStyles | null;
	protected formatPattern: string | null;

	protected concat: string | null = null;

	/**
	 * @param long
	 * 		string : dataKey fournissant l'URL
	 * 		OU ISearchFunc
	 */
	constructor(public long: string | ISearchFunc, public locale: string = "fr") {}

	setLong(long: string | ISearchFunc): this {
		this.long = long;
		return this;
	}

	/**
	 * Définit le caractère de concaténation d'une liste de valeurs transformées
	 * @param char
	 */
	setConcat(char: string): this {
		this.concat = char;
		return this;
	}

	/** Specif du format simplifié : date/time */
	setFormat(date: ELong2DateStyles | null, time: ELong2DateStyles | null): this {
		this.formatDate = date;
		this.formatTime = time;
		this.formatPattern = null;
		return this;
	}

	/** Specif du format complet par pattern */
	setFomatByPattern(pattern: string): this {
		this.formatPattern = pattern;
		return this;
	}

	toDom(holder: Element): Element {
		holder.setAttribute("func", "Long2Date");
		if (this.concat) holder.setAttribute("concat", this.concat);
		if (this.locale) holder.setAttribute("locale", this.locale);
		if (this.formatPattern) {
			holder.setAttribute("pattern", this.formatPattern);
		} else {
			if (this.formatDate) holder.setAttribute("dateStyle", this.formatDate);
			if (this.formatTime) holder.setAttribute("timeStyle", this.formatTime);
		}
		if (typeof this.long === 'string') {
			holder.setAttribute("dataKey", this.long || "");
		} else {
			holder.appendChild(this.long.toDom(<long/>));
		}
		return holder;
	}
}

export const enum ELong2DateStyles {
	short = 'short',
	full = 'full',
	medium = 'medium',
	long = 'long',
}

/**
 * Construction d'une cellule avec les responsabilités définies sur l'item
 */
export class SearchFuncResponsibles implements ISearchFunc {

	protected separator: string = ",";

	/**
	 * @param resp
	 */
	constructor(public resp: string) {}

	/**
	 * Définit le caractère de séparation d'une liste concaténée
	 * @param separator
	 */
	setSeparator(separator: string): this {
		this.separator = separator;
		return this;
	}

	toDom(holder: Element): Element {
		holder.setAttribute("func", "Responsibles");
		holder.setAttribute("separator", this.separator);
		holder.setAttribute("resp", this.resp);
		return holder;
	}
}

//----------- Taches ---------------

/**
 * Recherche de taches par des users impliqués
 * FIXME : gérer responsibilities, periodOp, ordering
 */
export class SearchTasksByUsers implements ISearchCrit {
	protected expandToGroups: boolean;
	protected expandToMembers: boolean;
	protected resps: string[];

	constructor(protected users?: string[], protected stages: EActStage[] = ['pending']) {}

	setUsers(...users: string[]): this {
		this.users = users;
		return this;
	}

	setExpandToGroups(expand: boolean): this {
		this.expandToGroups = true;
		return this;
	}

	setExpandToMembers(expand: boolean): this {
		this.expandToMembers = true;
		return this;
	}

	setResps(resps: string[]) {
		this.resps = resps;
		return this;
	}

	toDom(): Element | undefined {
		if (!this.users) return undefined;
		const expandUsers = [];
		if (this.expandToGroups) expandUsers.push("toGroups");
		if (this.expandToMembers) expandUsers.push("toMembers");
		return (this.stages?.length && this.users?.length) ?
			<exp type="TasksByUsers"
					 users={this.users.join(' ')}
					 expandUsers={expandUsers.length ? expandUsers.join(" ") : undefined}
					 responsibilities={this.resps?.length ? this.resps.join(" ") : undefined}
					 actStages={this.stages.join(" ")}/>
			: undefined;
	}
}

/**
 * Recherche toutes les tasks disponibles de l'atelier
 * FIXME : periodOp, period et ordering non gérés ici
 */
export class SearchTasksAll implements ISearchCrit {

	constructor(public stages: EActStage[]) {}

	toDom(): Element | undefined {
		return this.stages?.length ? <exp type="TasksAll" actStages={this.stages.join(" ")}/> : undefined;
	}
}

/**
 * SearchTasksDeadline
 */
export class SearchTasksDeadline implements ISearchCrit {

	static buildFromInputPeriod(input: IInputPeriod): SearchTasksDeadline | undefined {
		if (!input || !input.isSpecified()) return undefined;
		const lessThan = input.getLessThan(true);
		const moreThan = input.getMoreThan(true);
		if (moreThan !== null && lessThan !== null) return new SearchTasksDeadline("lt.lt", moreThan, lessThan,);
		return moreThan !== null ? new SearchTasksDeadline("gt", moreThan, null) : new SearchTasksDeadline("lt", lessThan, null);
	}

	constructor(public op?: EOperatorsSimple | EOperatorsIn, public startDate?: Date | number, public endDate?: Date | number) {};

	toDom(): Element | undefined {
		if (!this.startDate) return undefined;
		const vExp = <exp type="TasksDeadline" op={this.op}/>;
		let date = (typeof this.startDate === "number" ? this.startDate : this.startDate.getTime()).toString();
		if (this.endDate)
			date += "," + (typeof this.endDate === "number" ? this.endDate : this.endDate.getTime()).toString();
		vExp.setAttribute("value", date);
		return vExp;
	}
}

/**
 * SearchTasksScheduled
 */
export class SearchTasksScheduled implements ISearchCrit {

	static buildFromInputPeriod(input: IInputPeriod): SearchTasksScheduled | undefined {
		if (!input || !input.isSpecified()) return undefined;
		const lessThan = input.getLessThan(true);
		const moreThan = input.getMoreThan(true);
		if (moreThan !== null && lessThan !== null) return new SearchTasksScheduled("lt.lt", moreThan, lessThan,);
		return moreThan !== null ? new SearchTasksScheduled("gt", moreThan, null) : new SearchTasksScheduled("lt", lessThan, null);
	}

	constructor(public op?: EOperatorsSimple | EOperatorsIn, public startDate?: Date | number, public endDate?: Date | number) {};

	toDom(): Element | undefined {
		if (!this.startDate) return undefined;
		const vExp = <exp type="TasksScheduled" op={this.op}/>;
		let date = (typeof this.startDate === "number" ? this.startDate : this.startDate.getTime()).toString();
		if (this.endDate)
			date += "," + (typeof this.endDate === "number" ? this.endDate : this.endDate.getTime()).toString();
		vExp.setAttribute("value", date);
		return vExp;
	}
}

//-----------  StreamedSearch  -------------

/** Requete d'init ou de renouvellement d'une recherche (Client -> Server) */
type JStreamedSearchReq = {
	svc: 'search'
	wspCd: string				// Code du wsp
	search: string
	abort?: number 			// Recherche searchId précédente à annuler.
};


/** Réponse d'init d'une recherche (Server -> Client) */
type JStreamedSearchResp = {
	svc: 'search'
	searchId: number | undefined	// A utiliser par le client pour la demande de recherche suivantes annulant celle-ci (cf JStreamedSearchReq.abort).
	failure?: 'wspNotFound' | 'execFailed' // En cas d'echec, searchId est undefined.
};

/** Envoi d'un résultat partiel de recherche (Server -> Client) */
type JStreamedSearchResult = {
	svc: 'search'
	searchId: number 			// Recherche à laquelle ces résultats sont associés.
	rows: Dict<any>[]
	done?: true 				// Lorsque la recherche est achevée (en échec ou pas).
	failure?: 'execFailed'
};

/** Msg de demande d'annulation d'une recherche en cours sans en enclencher une nouvelle (Client -> Server) */
type JStreamedSearchAbort = {
	svc: 'search'
	abort: number 				// searchId à annuler.
};

/**
 * Gestionnaire d'une recherche évolutive en flux continu avec le serveur.
 */
export class StreamedSearch {

	public wsp: Wsp;

	public svc = 'search';

	public rowsHandler: (rows: Dict<any>[], done: boolean) => void | 'abort';

	protected _ws: IWsExecFrame;

	protected _onMsg: (m: Jsonisable) => void;

	protected _currentSearchId: number | undefined;

	protected _tmpSearchIdCounter = 0;

	constructor(wsp: Wsp, rowsHandler: (rows: Dict<any>[], done: boolean) => void | 'abort') {
		this.wsp = wsp;
		this.rowsHandler = rowsHandler;
		this._ws = wsp.wspServer.chain.wsFrames.ws;
		this._onMsg = this.onMsg.bind(this);
		this._ws.msgListeners.on(this.svc, this._onMsg);
	}

	/** Lance une nouvelle requête (et interrompt la précédente). Promesse rejetée en cas de requete invalide. */
	async search(search: SearchRequest) {
		const prevSearch = this._currentSearchId;
		const tmpSearchId = this._currentSearchId = --this._tmpSearchIdCounter; //id temporaires sont < 0
		const resp = await this._ws.sendReq<JStreamedSearchResp>({svc: this.svc, wspCd: this.wsp.code, search: search.toXml(), abort: prevSearch >= 0 ? prevSearch : undefined} as JStreamedSearchReq);
		if (resp.failure) {
			console.trace("Search failed", search.toXml(), resp);
			throw resp.failure;
		} else if (this._currentSearchId === tmpSearchId) { //racecond
			this._currentSearchId = resp.searchId;
		}
	}

	/** Abandonne la requete en cours. */
	abortSearch() {
		if (this._currentSearchId) {
			this._ws.sendMsg({svc: this.svc, abort: this._currentSearchId} as JStreamedSearchAbort);
			this._currentSearchId = undefined;
		}
	}

	/** Clos ce StreamedSearch. Objet plus utilisable. */
	close() {
		if (this._currentSearchId) try {
			this.abortSearch();
		} catch (e) {}
		this._ws.msgListeners.removeListener(this.svc, this._onMsg);
		//help gc
		this._ws = null;
		this._onMsg = null;
		this.wsp = null;
		this.rowsHandler = null;
	}

	protected onMsg(m: JStreamedSearchResult) {
		if (m.searchId === this._currentSearchId) {
			if (m.done) this._currentSearchId = undefined;
			if (this.rowsHandler(m.rows, m.done) === 'abort') this.abortSearch();
		}
	}
}

